import React, { useState, useEffect, useRef } from "react";
import "./style.sass";

export const CustomDatePicker = ({
  value = "",
  onChange,
  placeholder = "Select date",
  className = "",
  style = {},
  dropUp = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Parse initial selected date
  const parseValueToDate = (valStr) => {
    if (!valStr || typeof valStr !== "string") return null;
    const clean = valStr.split("T")[0].trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return null;
    const [y, m, d] = clean.split("-").map((n) => parseInt(n, 10));
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m - 1, d);
  };

  const selectedDate = parseValueToDate(value);

  // View state for calendar (current displayed year & month)
  const today = new Date();
  const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : today.getMonth());

  // Sync view when selected value changes
  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const shortMonthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  // Format date for display in input
  const getDisplayText = () => {
    if (!selectedDate) return placeholder;
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const monthStr = shortMonthNames[selectedDate.getMonth()];
    const year = selectedDate.getFullYear();
    return `${day} ${monthStr} ${year}`;
  };

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDay = (year, month, day, e) => {
    e.stopPropagation();
    const formattedY = String(year);
    const formattedM = String(month + 1).padStart(2, "0");
    const formattedD = String(day).padStart(2, "0");
    const dateStr = `${formattedY}-${formattedM}-${formattedD}`;
    if (onChange) onChange(dateStr);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) onChange("");
    setIsOpen(false);
  };

  const handleSelectToday = (e) => {
    e.stopPropagation();
    const now = new Date();
    const formattedY = String(now.getFullYear());
    const formattedM = String(now.getMonth() + 1).padStart(2, "0");
    const formattedD = String(now.getDate()).padStart(2, "0");
    const dateStr = `${formattedY}-${formattedM}-${formattedD}`;
    if (onChange) onChange(dateStr);
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setIsOpen(false);
  };

  // Generate matrix for calendar grid cells (dynamically 28, 35, or 42 slots)
  const generateGrid = () => {
    const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay();
    const daysInCurrentMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells = [];

    // 1. Previous month trailing days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      cells.push({
        day: daysInPrevMonth - i,
        month: viewMonth === 0 ? 11 : viewMonth - 1,
        year: viewMonth === 0 ? viewYear - 1 : viewYear,
        isCurrentMonth: false,
      });
    }

    // 2. Current month days
    for (let d = 1; d <= daysInCurrentMonth; d++) {
      cells.push({
        day: d,
        month: viewMonth,
        year: viewYear,
        isCurrentMonth: true,
      });
    }

    // 3. Next month leading days
    const targetCount = cells.length > 35 ? 42 : cells.length > 28 ? 35 : 28;
    const remainingSlots = targetCount - cells.length;
    for (let d = 1; d <= remainingSlots; d++) {
      cells.push({
        day: d,
        month: viewMonth === 11 ? 0 : viewMonth + 1,
        year: viewMonth === 11 ? viewYear + 1 : viewYear,
        isCurrentMonth: false,
      });
    }

    return cells;
  };

  const gridCells = generateGrid();

  const isTodayCell = (c) => {
    return (
      c.day === today.getDate() &&
      c.month === today.getMonth() &&
      c.year === today.getFullYear()
    );
  };

  const isSelectedCell = (c) => {
    if (!selectedDate) return false;
    return (
      c.day === selectedDate.getDate() &&
      c.month === selectedDate.getMonth() &&
      c.year === selectedDate.getFullYear()
    );
  };

  return (
    <div
      className={`custom-datepicker-container ${dropUp ? "drop-up" : ""} ${className}`}
      ref={containerRef}
      style={style}
    >
      {/* Trigger Button */}
      <div
        className={`custom-datepicker-trigger ${isOpen ? "active" : ""} ${
          value ? "has-val" : ""
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {value ? (
          <span className="selected-val">{getDisplayText()}</span>
        ) : (
          <span className="placeholder-val">{placeholder}</span>
        )}

        <div className="trigger-actions">
          {value && (
            <span
              className="clear-icon-btn"
              onClick={handleClear}
              title="Clear date"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
          )}
          <svg
            className="calendar-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={value ? "#612EF7" : "#667085"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
      </div>

      {/* Popover Calendar Card */}
      {isOpen && (
        <div
          className={`custom-calendar-popover ${dropUp ? "pos-up" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Calendar Header */}
          <div className="calendar-header">
            <span className="month-year-title">
              {monthNames[viewMonth]}, {viewYear}
            </span>

            <div className="nav-arrows">
              <button
                type="button"
                className="nav-btn"
                onClick={handlePrevMonth}
                title="Previous Month"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className="nav-btn"
                onClick={handleNextMonth}
                title="Next Month"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>

          {/* Day Names Row */}
          <div className="days-header">
            {dayNames.map((dayName, i) => (
              <div key={i} className="day-name">
                {dayName}
              </div>
            ))}
          </div>

          {/* Dynamic Days Grid */}
          <div className="days-grid">
            {gridCells.map((c, idx) => {
              const isSelected = isSelectedCell(c);
              const isToday = isTodayCell(c);
              return (
                <div
                  key={idx}
                  className={`grid-cell ${
                    c.isCurrentMonth ? "current-month" : "other-month"
                  } ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
                  onClick={(e) =>
                    handleSelectDay(c.year, c.month, c.day, e)
                  }
                >
                  <span>{c.day}</span>
                </div>
              );
            })}
          </div>

          {/* Footer Actions */}
          <div className="calendar-footer">
            <button
              type="button"
              className="footer-btn clear-btn"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="footer-btn today-btn"
              onClick={handleSelectToday}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
